/* ============================================================
   PSYCO — Early Access page behaviour
   Added 2026-08-28.

   Three things, all vanilla:
     1. CTA click  -> opens the live Klaviyo form (WDMUxY)
     2. Odometer   -> sliding-digit member count, animates on scroll-in
     3. Countdown  -> D/H/M/S to the drop

   WHY NOT THE REACT COMPONENT: this is a Shopify Liquid theme — no
   React, no Tailwind, no build step. The suggested count-up needed
   `motion` + `react-use-measure` (~50KB+ of JS) on a site already
   fighting a PageSpeed regression. This reproduces the same odometer
   roll in a few hundred bytes, with no dependencies.

   Loaded with defer, so it is never render-blocking.
   ============================================================ */
(function () {
  if (window.__psycoEarlyAccessBound) return;
  window.__psycoEarlyAccessBound = true;

  var reduceMotion =
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ── 1. CTA opens the Klaviyo pop-up ─────────────────────────
     Document-level delegation: Shopify re-injects section markup via
     innerHTML, which drops element-bound listeners. Delegation survives. */
  document.addEventListener('click', function (e) {
    var cta = e.target && e.target.closest ? e.target.closest('.pea-cta') : null;
    if (!cta) return;
    var formId = cta.getAttribute('data-klaviyo-form');
    if (!formId) return;
    /* If Klaviyo is blocked, do NOT preventDefault — let the href run so
       the button is never a dead click. */
    if (window._klOnsite && typeof window._klOnsite.push === 'function') {
      e.preventDefault();
      window._klOnsite.push(['openForm', formId]);
    }
  });

  /* ── 2. Odometer count-up ────────────────────────────────────
     Each digit is a column of eleven cells (0-9 plus a repeated 0). One
     digit therefore equals 100/11 % of the strip, NOT 10% — getting that
     wrong makes every column drift and settle between two numbers, which
     reads as "frozen".

     Each column is animated independently from 0 to (finalDigit + 10*spins)
     so it ALWAYS lands on an exact integer. Deriving the position from the
     running total instead — (value / place) % 10 — leaves every column
     except the units resting on a fraction, which is the same visual bug. */
  var CELL = 100 / 11; /* % of strip per digit */

  function buildOdometer(el) {
    var target = parseInt(el.getAttribute('data-count'), 10);
    if (!isFinite(target) || target < 0) return;

    var text = target.toLocaleString('en-US'); /* e.g. "2,935" */
    el.textContent = '';
    /* Screen readers get the final number once, as text. The rolling
       columns are decorative and would otherwise be read as digit soup. */
    el.setAttribute('role', 'img');
    el.setAttribute('aria-label', text + ' members');

    var digitCount = 0;
    var i;
    for (i = 0; i < text.length; i++) {
      if (text[i] >= '0' && text[i] <= '9') digitCount++;
    }

    var cols = [];
    var seen = 0;
    for (i = 0; i < text.length; i++) {
      var ch = text[i];
      if (ch < '0' || ch > '9') {
        var sep = document.createElement('span');
        sep.className = 'pea-odo__sep';
        sep.setAttribute('aria-hidden', 'true');
        sep.textContent = ch;
        el.appendChild(sep);
        continue;
      }

      var slot = document.createElement('span');
      slot.className = 'pea-odo__slot';
      slot.setAttribute('aria-hidden', 'true');

      var strip = document.createElement('span');
      strip.className = 'pea-odo__strip';
      for (var d = 0; d <= 10; d++) {
        var cell = document.createElement('span');
        cell.className = 'pea-odo__cell';
        cell.textContent = String(d % 10);
        strip.appendChild(cell);
      }
      slot.appendChild(strip);
      el.appendChild(slot);

      /* Lower places spin more, so the number blurs in from the right and
         settles left-to-right like a real odometer. */
      var fromRight = digitCount - 1 - seen;
      cols.push({
        strip: strip,
        travel: parseInt(ch, 10) + 10 * (fromRight + 1)
      });
      seen++;
    }

    function render(progress) {
      for (var k = 0; k < cols.length; k++) {
        var pos = cols[k].travel * progress;
        /* Land inside [0,10): 9->10 sits on the repeated 0 cell, so the
           wrap is seamless and never snaps backwards. */
        var cyclePos = pos % 10;
        cols[k].strip.style.transform =
          'translateY(' + -(cyclePos * CELL) + '%)';
      }
    }

    if (reduceMotion) {
      render(1);
      return;
    }

    render(0);

    var DURATION = 1800;
    var started = false;

    function run() {
      if (started) return;
      started = true;
      var t0 = null;
      function frame(ts) {
        if (t0 === null) t0 = ts;
        var p = (ts - t0) / DURATION;
        if (p >= 1) {
          render(1); /* exact integers, no residual fraction */
          return;
        }
        /* easeOutExpo — quick start, long settle */
        render(1 - Math.pow(2, -10 * p));
        requestAnimationFrame(frame);
      }
      requestAnimationFrame(frame);
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(
        function (entries) {
          for (var n = 0; n < entries.length; n++) {
            if (entries[n].isIntersecting) {
              run();
              io.disconnect();
            }
          }
        },
        { threshold: 0.4 }
      );
      io.observe(el);
      /* Fallback: if it is already on screen at load some browsers fire
         late or not at all after a bfcache restore. */
      setTimeout(run, 2500);
    } else {
      run();
    }
  }

  /* ── 3. Countdown to the drop ────────────────────────────────
     Same epoch as the announcement bar: 2026-09-04 18:00 UTC = noon Denver. */
  function initCountdown(root) {
    var target = parseInt(root.getAttribute('data-drop-epoch'), 10) * 1000;
    if (!isFinite(target)) return;

    var units = {
      d: root.querySelector('[data-cd="d"]'),
      h: root.querySelector('[data-cd="h"]'),
      m: root.querySelector('[data-cd="m"]'),
      s: root.querySelector('[data-cd="s"]')
    };
    var live = root.querySelector('[data-cd="live"]');
    var timer = null;

    function pad(n) { return n < 10 ? '0' + n : String(n); }

    function tick() {
      var diff = target - Date.now();
      if (diff <= 0) {
        if (timer) { clearInterval(timer); timer = null; }
        root.setAttribute('data-state', 'live');
        if (live) live.textContent = 'SEASON 14 IS LIVE';
        return;
      }
      var secs = Math.floor(diff / 1000);
      if (units.d) units.d.textContent = pad(Math.floor(secs / 86400));
      if (units.h) units.h.textContent = pad(Math.floor(secs / 3600) % 24);
      if (units.m) units.m.textContent = pad(Math.floor(secs / 60) % 60);
      if (units.s) units.s.textContent = pad(secs % 60);
    }

    tick();
    timer = setInterval(tick, 1000);
    /* A sleeping phone stops firing intervals — resync on wake. */
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) tick();
    });
  }

  function init() {
    var odos = document.querySelectorAll('.pea-odo');
    for (var i = 0; i < odos.length; i++) buildOdometer(odos[i]);
    /* Two shapes of clock: the standalone block, and one living INSIDE the
       CTA button. Deliberately NOT a bare [data-drop-epoch] query — the
       announcement bar uses that attribute too and has its own script. */
    var cds = document.querySelectorAll('.pea-countdown, .pea-cta[data-drop-epoch]');
    for (var j = 0; j < cds.length; j++) initCountdown(cds[j]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
